/* @@PARTPARSE_START@@ — extracted by qa/merge_props.js, so the parser the property battery
   proves is the parser the app actually runs. */
/* ══ v14 (W4a): READING A PART — PARSED, NEVER EXECUTED ════════════════════════════════════
   ⚠ THIS EXISTS BECAUSE THE OBVIOUS ROUTE IS A CODE-EXECUTION VECTOR. §v14 says parts are
   parsed "through the import parser's path", and the import parser is `_parseImportText`, which
   works by `new Function('window', text)(sandbox)`. That IS execution: shadowing `window` with
   a parameter stops accidental writes to the real one, but a `new Function` body is compiled in
   GLOBAL scope, so `document`, `location`, `fetch`, `localStorage` and `indexedDB` are all
   reachable from it. Tolerable for Import — one file, chosen by the user, one click. Not
   tolerable for boot, which will read EVERY part in a shared OneDrive folder with no click at
   all, which is precisely the "five colleagues' files would be five code-execution vectors"
   the scope names as the reason for parsing in the first place. So the intent is honoured
   rather than the letter, and `_parseImportText` is left alone for user-picked files.

   WHY A REAL PARSER IS CHEAP HERE, rather than a JS parser in miniature. Parts are only ever
   written by `serializeDB`, whose format is fully known: exactly four possible top-level keys,
   each on its own line at two-space indent, and EVERYTHING nested goes through JSON.stringify.
   That second fact is the load-bearing one — JSON.stringify escapes newlines inside strings as
   the two characters \\n, so **every real newline in a part file is structural**. Line-anchored
   matching therefore cannot be fooled by file CONTENT, which is the same guarantee the
   launcher's own regexes have relied on since v10.5. Each value then goes through JSON.parse.
   Nothing is compiled, nothing is evaluated, and a top-level key we do not recognise THROWS
   rather than being skipped — the MERGE_TOP_KEYS discipline, for the same reason: silently
   discarding a colleague's data is the failure this design exists to prevent.

   ⚠ FORMAT ONLY. The schema gate is deliberately NOT here: ◆7 refuses an out-of-date part
   ALONE and names who is behind, which is a decision for the caller with the roster in hand,
   not for a text parser. */

/* Every top-level key that is not `projects`, with the exact line shape serializeDB emits. */
const PART_SCALARS = [
  { key: 'schemaVersion', re: /^  schemaVersion: (-?\d+),$/ },
  { key: 'savedAt',       re: /^  savedAt: ("(?:[^"\\]|\\.)*"),$/ },
  { key: 'writer',        re: /^  writer: ("(?:[^"\\]|\\.)*"),$/ },
  { key: 'programmes',    re: /^  programmes: (\[.*\]),$/ }
];
const PART_PREFIX = 'window.__DB__ = {\n';
const PART_SUFFIX = '\n};';
const PART_PJ_OPEN = '  projects: [\n';
const PART_PJ_CLOSE = '\n  ]';

function _parsePartText(text, label) {
  const at = label ? (' in ' + label) : '';
  if (typeof text !== 'string' || !text.trim()) throw new Error('That file is empty' + at + '.');
  /* Line endings normalised on the READ side only, exactly as the launcher does: the app always
     writes \n, but a part that has been through a Windows editor may not have. */
  const t = text.replace(/\r\n/g, '\n');

  /* 1. Only whitespace and block comments may precede the assignment. Anything else — a
        statement, a second assignment, an IIFE — is refused rather than ignored. */
  let i = 0;
  for (;;) {
    while (i < t.length && /\s/.test(t.charAt(i))) i++;
    if (t.charAt(i) === '/' && t.charAt(i + 1) === '*') {
      const e = t.indexOf('*/', i + 2);
      if (e === -1) throw new Error('Unterminated comment' + at + '.');
      i = e + 2;
      continue;
    }
    break;
  }
  if (t.slice(i, i + PART_PREFIX.length) !== PART_PREFIX)
    throw new Error('That is not a TrendAI data file' + at + ' (no window.__DB__ assignment).');

  /* 2. …and nothing at all may follow the closing brace. */
  let end = t.length;
  while (end > 0 && /\s/.test(t.charAt(end - 1))) end--;
  if (t.slice(end - PART_SUFFIX.length, end) !== PART_SUFFIX)
    throw new Error('That file does not end where a TrendAI data file ends' + at + '.');
  const body = t.slice(i + PART_PREFIX.length, end - PART_SUFFIX.length);

  /* 3. `projects` is last and runs to the end of the object. Split it off; what precedes it is
        the scalar block. */
  let pStart;
  if (body.slice(0, PART_PJ_OPEN.length) === PART_PJ_OPEN) pStart = 0;
  else {
    const k = body.indexOf('\n' + PART_PJ_OPEN);
    if (k === -1) throw new Error('No projects array' + at + '.');
    pStart = k + 1;
  }
  if (body.slice(body.length - PART_PJ_CLOSE.length) !== PART_PJ_CLOSE)
    throw new Error('The projects array is not closed as expected' + at + '.');
  const inner = body.slice(pStart + PART_PJ_OPEN.length, body.length - PART_PJ_CLOSE.length);

  const out = {};
  const lines = pStart ? body.slice(0, pStart - 1).split('\n') : [];
  for (let n = 0; n < lines.length; n++) {
    let hit = null;
    for (let s = 0; s < PART_SCALARS.length && !hit; s++) {
      const m = PART_SCALARS[s].re.exec(lines[n]);
      if (m) hit = { key: PART_SCALARS[s].key, raw: m[1] };
    }
    if (!hit) throw new Error('Unexpected line in the data file' + at + ': ' + JSON.stringify(lines[n].slice(0, 60)));
    if (Object.prototype.hasOwnProperty.call(out, hit.key))
      throw new Error('Duplicate "' + hit.key + '"' + at + '.');
    out[hit.key] = JSON.parse(hit.raw);
  }
  out.projects = JSON.parse('[' + inner + ']');

  /* 4. Shape, so a malformed file fails HERE rather than three renders later. */
  if (typeof out.schemaVersion !== 'number')
    throw new Error('That file does not state a schema version' + at + '.');
  if (!Array.isArray(out.projects)) throw new Error('projects is not a list' + at + '.');
  if (out.programmes !== undefined && !Array.isArray(out.programmes))
    throw new Error('programmes is not a list' + at + '.');
  return out;
}
/* @@PARTPARSE_END@@ */

/* @@SERIALIZE_START@@ — extracted by qa/merge_props.js so the byte-for-byte convergence
   test uses the REAL serialiser rather than a stand-in. */
function serializeDB(db) {
  /* ⚠⚠ v15.2: A PROJECT TOMBSTONE IS EMITTED WHOLESALE, ON ONE LINE — because the template
     below CANNOT serialise one, and that broke the file. A tombstone is {del, rev, writer, uid}
     with no name, no arrays, no appMeta, and JSON.stringify(undefined) interpolates as the bare
     token `undefined` — NINE invalid-JSON sites per tombstone (the exact class A14's comment
     below names, executed and proven in the v15.2 round). Consequence before this fix: the
     FIRST save after deleting a project wrote a part `_parsePartText` REFUSES, the post-save
     re-read then adopted a portfolio WITHOUT my part (work visibly rolled back seconds after
     the "Saved" toast), and every boot after refused the file by name. Live since v14 shipped
     tombstones. The parser needs no change — it JSON.parses the whole array — and the
     round-trip stays byte-exact because JSON.parse preserves key order and this same line
     re-emits it. */
  const parts = db.projects.map(c => {
    if (c && c.del) return '    ' + JSON.stringify(c);
    /* ── v16.1 (d): REFUSE on the identity fields, never default them ─────────────────
       The guards below default a missing COLLECTION to empty, which is safe. Identity is
       different: the merge groups projects BY `uid`, so a defaulted one would manufacture a
       record identity and silently fuse two projects. That is worse than a loud failure and
       worse than the unreadable file this whole item is about.
       ⚠ THIS IS THE ONE PLACE v16.1 TOUCHES "Save is never refused", so the boundary is
       stated here: that law is about never refusing a save because of MERGE STATE or a
       colleague's part. A structurally impossible record is a different thing — nothing is
       written, the previous good file survives untouched, and the user is told which project
       is at fault. Realistically this can never fire: the fields at risk from a mixed-version
       team are FUTURE ones, and id/uid/name have existed throughout. Belt and braces. */
    for (let _i = 0; _i < 3; _i++) {
      const _k = ['id', 'uid', 'name'][_i];
      if (c[_k] === undefined)
        throw new Error('Cannot save: a project is missing its "' + _k + '". Refusing to '
          + 'write a file this app could not read back — nothing was written and your '
          + 'previous file is untouched.');
    }
    return [
    "    {",
    `      "id": ${JSON.stringify(c.id)},`,
    `      "uid": ${JSON.stringify(c.uid)},`,
    `      "name": ${JSON.stringify(c.name)},`,
    `      "archived": ${JSON.stringify(!!c.archived)},`,
    `      "actions": ${JSON.stringify(c.actions || [], null, 2)},`,
    `      "sessions": ${JSON.stringify(c.sessions || [], null, 2)},`,
    `      "stakeholders": ${JSON.stringify(c.stakeholders || [], null, 2)},`,
    `      "risks": ${JSON.stringify(c.risks || [], null, 2)},`,
    /* v13.1 (A14), second line of defence: JSON.stringify(undefined) returns the VALUE
       undefined, which this template then interpolates as the bare token `undefined` — not
       valid JSON, and a silent corruption of the file. The real fix is upstream (Import's
       defensive-defaults list now names `assumptions`).
       ⚠⚠ v16.1 (d) CORRECTION: this comment used to claim the guard made the class
       "impossible from any path". IT DID NOT — A14 guarded `assumptions` ONLY, while actions,
       sessions, stakeholders, risks, workstreams, edits and appMeta stayed bare, and a
       real-data census three versions later found the hole exactly there. A comment claiming
       total coverage above a partial guard is WHY it survived that long. Every collection is
       guarded now and the identity fields refuse outright; the claim is finally true, and it
       is asserted by a loop over the emit list in qa/merge_props.js rather than trusted.
       Byte-proven safe: an empty array is truthy, so every present value —
       including [] — serialises exactly as before, and the census found no project on disk
       missing the key. */
    `      "assumptions": ${JSON.stringify(c.assumptions || [], null, 2)},`,
    `      "workstreams": ${JSON.stringify(c.workstreams || [], null, 2)},`,
    `      "edits": ${JSON.stringify(c.edits || {}, null, 2)},`,
    /* v10.3 (W1, ◆6 variant): emit newActions VERBATIM instead of a hardcoded [].
       exportFile bakes the ACTIVE project's pending adds into `actions` and empties
       `newActions` before this runs, so the active project still writes [] — byte-identical
       to every earlier version. The difference is a NON-active project: its pending adds
       used to be dropped here (surviving only as orphan `edits` entries, which the next
       load pruned), so saving while another project was open silently destroyed them.
       Chosen over ◆6's "flush on switch" because newActions membership drives the visible
       "new" tag, and flushing would have silently removed it. */
    `      "newActions": ${JSON.stringify(c.newActions || [], null, 2)},`,
    /* v11: which programme owns this project. A new TOP-LEVEL per-project field, so
       unlike a key inside appMeta it needs this one line in the ordered list. */
    `      "programmeId": ${JSON.stringify(c.programmeId || '')},`,
    /* v14 (W2): a project's OWN fields go through this hand-picked list, so unlike a key
       inside appMeta or a nested collection, `rev`/`writer`/`del` on a PROJECT would be
       silently dropped without these lines. Emitted ONLY when present, so every existing
       file serialises byte-identically and the keys stay LAZY. */
    /* v16.5 (d): the project's visible number. A project's own fields go through this
       hand-picked list, so a new top-level key is DROPPED without a line here — measured on
       the v16.4 shell, where a project key vanished on save while a programme key (written
       wholesale) survived. Emitted ONLY when present, so a file written before numbering
       existed still round-trips byte-identically and the key stays LAZY. */
    ...(c.displayNo === undefined ? [] : [`      "displayNo": ${JSON.stringify(c.displayNo)},`]),
    ...(c.rev    === undefined ? [] : [`      "rev": ${JSON.stringify(c.rev)},`]),
    ...(c.writer === undefined ? [] : [`      "writer": ${JSON.stringify(c.writer)},`]),
    ...(c.del    === undefined ? [] : [`      "del": ${JSON.stringify(c.del)},`]),
    `      "appMeta": ${JSON.stringify(c.appMeta || {})}`,
    "    }"
    ].join("\n");
  }).join(",\n");
  const _savedAt = (typeof db.savedAt === 'string' && db.savedAt) ? `  savedAt: ${JSON.stringify(db.savedAt)},\n` : '';
  /* ── v14 (W4a): WHOSE PART IS THIS? ──────────────────────────────────────────────────
     The merge reads each RECORD's own `writer` for the tie-break, and falls back to the
     PART's writer for a record that carries none — which is what keeps the merge associative,
     since an already-merged result holds records from several writers. So the file has to say
     who wrote it, and `MERGE_TOP_KEYS` has listed `writer` since W1 in anticipation.
     ⚠ LAZY, like rev/writer/del on a project: emitted only when it is actually known, so a
     file written before identity existed still round-trips byte-identically and the
     roundtrip_byte_exact canary is untouched. Deriving it from the FILENAME instead was
     rejected — renaming a part would then silently change its author. */
  const _writer = (typeof db.writer === 'string' && db.writer) ? `  writer: ${JSON.stringify(db.writer)},\n` : '';
  /* v10.0: the programme branch — the first top-level data outside projects[] (schema 8).
     Serialised WHOLESALE (one compact line, the appMeta precedent), so any future key
     inside programme rides along with no further serialiser change. */
  const _programme = Array.isArray(db.programmes) ? `  programmes: ${JSON.stringify(db.programmes)},\n` : '';
  return "/* TrendAI FlightDeck — combined data file (" + APP_VERSION + ")\n" +
         "   Loaded by the shell via <script src>. The app rewrites this on Save. */\n" +
         `window.__DB__ = {\n  schemaVersion: ${db.schemaVersion},\n${_savedAt}${_writer}${_programme}  projects: [\n${parts}\n  ]\n};\n`;
}
/* @@SERIALIZE_END@@ */
